import { Info } from "lucide-react";
import { useEffect, useRef } from "react";
import { Toaster as Sonner } from "sonner";
import { Lordicon, type LordiconRef } from "@/components/ui/lordicon";
import { Spinner } from "@/components/ui/spinner";
import { useTheme } from "@/components/ui/theme-context";

/** Wrapper that auto-plays a Lordicon animation on mount — used for toast icons */
function AutoPlayLordicon({
  icon,
  size,
  colors,
}: {
  icon: string;
  size: number;
  colors: { primary: string; secondary: string };
}) {
  const ref = useRef<LordiconRef>(null);

  useEffect(() => {
    const timer = setTimeout(() => ref.current?.playFromBeginning(), 50);
    return () => clearTimeout(timer);
  }, []);

  return <Lordicon ref={ref} icon={icon} size={size} colors={colors} />;
}

// Toast icon colors. Literal hex = the success/destructive/warning ramp -600/-500
// steps (primary darker, secondary lighter — a two-tone Lordicon animation).
// Hex, not the token var(), is required: Lordicon feeds these into a Lottie
// `primary:…,secondary:…` color string that needs a RESOLVED color — var()/oklch
// don't resolve there (icons fall back to default). Sanctioned literal-hex
// exception, same as ui/lordicon.tsx; keep in sync with the success/destructive/
// warning ramps if those values change.
const TOAST_ICON_COLORS = {
  success: {
    primary: "#16a34a", // ≈ --success-600
    secondary: "#22c55e", // ≈ --success-500
  },
  error: {
    primary: "#dc2626", // ≈ --destructive (red-600)
    secondary: "#ef4444", // red-500
  },
  warning: {
    primary: "#d97706", // ≈ --warning-600
    secondary: "#f59e0b", // ≈ --warning-500
  },
} as const;

function Toaster() {
  const { theme } = useTheme();

  return (
    <Sonner
      theme={theme}
      position="top-right"
      // Clear the sticky top nav (h-16 = 64px + safe-area inset) so toasts don't
      // cover the top-right cluster (super Shield button + info popover). Only the
      // `top` side is overridden — a bare string would apply to all four sides and
      // push toasts off the right edge; the object form leaves right/bottom/left at
      // Sonner's defaults. mobileOffset is set explicitly because below Sonner's
      // 600px breakpoint it does NOT inherit `offset` (defaults to 16px), so
      // mobile/Capacitor viewports would otherwise keep overlapping the nav.
      offset={{ top: "calc(env(safe-area-inset-top) + 72px)" }}
      mobileOffset={{ top: "calc(env(safe-area-inset-top) + 72px)" }}
      duration={2000}
      gap={8}
      closeButton
      icons={{
        success: (
          <AutoPlayLordicon
            icon="target"
            size={20}
            colors={TOAST_ICON_COLORS.success}
          />
        ),
        error: (
          <AutoPlayLordicon
            icon="error"
            size={20}
            colors={TOAST_ICON_COLORS.error}
          />
        ),
        warning: (
          <AutoPlayLordicon
            icon="alert"
            size={20}
            colors={TOAST_ICON_COLORS.warning}
          />
        ),
        info: <Info className="size-5" />,
        loading: <Spinner className="size-5" />,
      }}
      toastOptions={{
        classNames: {
          toast:
            "group flex items-center gap-3 rounded-lg border px-4 py-3 shadow-lg backdrop-blur-sm transition-all",
          title: "text-sm font-medium",
          description: "text-xs opacity-80",
          actionButton:
            "bg-primary text-primary-foreground rounded-md px-3 py-1.5 text-xs font-medium",
          cancelButton:
            "bg-muted text-muted-foreground rounded-md px-3 py-1.5 text-xs font-medium",
          icon: "shrink-0",
          closeButton:
            "border-border bg-background text-muted-foreground hover:text-foreground hover:bg-muted transition-colors",
          success:
            "bg-success-50 dark:bg-success-500/10 border-success-200 dark:border-success-500/20 text-success-800 dark:text-success-400 [&_[data-icon]]:text-success-600 dark:[&_[data-icon]]:text-success-400",
          error:
            "bg-destructive/8 dark:bg-destructive/10 border-destructive/20 text-danger-800 dark:text-danger-400 [&_[data-icon]]:text-danger-600 dark:[&_[data-icon]]:text-danger-400",
          warning:
            "bg-warning-50 dark:bg-warning-500/10 border-warning-200 dark:border-warning-500/20 text-warning-800 dark:text-warning-400 [&_[data-icon]]:text-warning-600 dark:[&_[data-icon]]:text-warning-400",
          info: "bg-info/8 dark:bg-info/10 border-info/20 text-info-foreground [&_[data-icon]]:text-info-foreground",
          loading:
            "bg-muted/50 border-border text-muted-foreground [&_[data-icon]]:text-muted-foreground",
          default: "bg-background border-border text-foreground",
        },
      }}
    />
  );
}

export { Toaster };
